/**
 * Domain-aware presentation components.
 *
 * These know about orders and money, which the shadcn primitives deliberately do
 * not. They live in the shared UI package rather than in an app because both
 * apps render the same order status, the same price treatment, and the same
 * order code — and those three things must look identical on both sides of the
 * counter.
 *
 * Every tone maps to a whole literal class string. Tailwind reads class names
 * from source text, so a name assembled at runtime would never be generated; see
 * `TONE_SOFT` and its siblings in `lib/theme.ts`.
 */

import type { ReactNode } from 'react';
import { View } from 'react-native';

import { formatMoney, orderStatusLabel, orderStatusTone, type OrderStatus } from '@yummy/shared';

import { Text } from './ui/text';
import { Row } from './layout';
import { useT } from '../providers/locale';
import { cn } from '../lib/utils';
import { TONE_SOFT, TONE_SOLID, TONE_TEXT, type Tone } from '../lib/theme';

/** How a chip carries its tone. */
export type Emphasis = 'soft' | 'solid' | 'outline';

const CHIP_SIZE = {
  sm: 'px-2 py-0.5',
  md: 'px-3 py-1',
} as const;

const CHIP_TEXT_SIZE = {
  sm: 'text-[11px]',
  md: 'text-xs',
} as const;

export interface ToneBadgeProps {
  label: string;
  tone?: Tone;
  /** `solid` fills with the tone; `soft` tints the background. */
  emphasis?: Emphasis;
  size?: keyof typeof CHIP_SIZE;
  className?: string;
}

/**
 * A compact status chip.
 *
 * shadcn's Badge is a rounded rectangle rather than a pill, and this keeps that:
 * the pill shape is reserved for counts, which read better circular.
 *
 * @param props Label, tone, and emphasis.
 * @returns A tone-coloured chip.
 */
export function ToneBadge({
  label,
  tone = 'neutral',
  emphasis = 'soft',
  size = 'md',
  className,
}: ToneBadgeProps) {
  return (
    <View
      className={cn(
        'self-start rounded-md',
        CHIP_SIZE[size],
        emphasis === 'soft' && TONE_SOFT[tone],
        emphasis === 'solid' && TONE_SOLID[tone],
        emphasis === 'outline' && 'border-border border bg-transparent',
        className
      )}>
      <Text
        numberOfLines={1}
        className={cn(
          'font-semibold',
          CHIP_TEXT_SIZE[size],
          emphasis === 'solid' ? 'text-primary-foreground' : TONE_TEXT[tone]
        )}>
        {label}
      </Text>
    </View>
  );
}

/**
 * A numeric count, rendered as a circular pill.
 *
 * @param props The count and tone.
 * @returns The pill.
 */
export function CountBadge({ count, tone = 'brand' }: { count: number; tone?: Tone }) {
  return (
    <View
      className={cn(
        'min-w-5 items-center justify-center rounded-full px-1.5 py-px',
        TONE_SOLID[tone]
      )}>
      <Text className="text-primary-foreground text-[11px] font-bold">{count}</Text>
    </View>
  );
}

/**
 * The status chip for an order.
 *
 * @param props Order status and optional size override.
 * @returns A tone-coloured chip reading the status label.
 */
export function StatusChip({
  status,
  size = 'md',
}: {
  status: OrderStatus;
  size?: keyof typeof CHIP_SIZE;
}) {
  const t = useT();
  return (
    <ToneBadge
      label={orderStatusLabel(status, t)}
      tone={orderStatusTone(status)}
      // `ready` is the one state a merchant must not miss, so it is filled
      // rather than tinted.
      emphasis={status === 'ready' ? 'solid' : 'soft'}
      size={size}
    />
  );
}

// Size *and* weight per variant: a price is the same fact whichever screen
// shows it, and the customer's menu row wanted 16/600 where the dashboard's
// price is still bold.
const PRICE_SIZE = {
  /** A price beside its own name, as a menu row has it. */
  caption: 'text-base font-semibold tabular-nums',
  body: 'text-base font-bold',
  subheading: 'text-lg font-bold',
  heading: 'text-xl font-bold',
} as const;

/**
 * A price, with the original struck through when discounted.
 *
 * @param props Amounts and currency.
 * @returns The price treatment.
 */
export function Price({
  cents,
  currency,
  compareAtCents,
  variant = 'body',
  className,
}: {
  cents: number;
  currency: string;
  /** Original price; rendered struck through when greater than `cents`. */
  compareAtCents?: number | null;
  variant?: keyof typeof PRICE_SIZE;
  className?: string;
}) {
  const discounted = compareAtCents != null && compareAtCents > cents;

  return (
    <Row gap="xs" align="baseline" className={className}>
      <Text
        className={cn(
          PRICE_SIZE[variant],
          discounted ? 'text-primary-strong' : 'text-foreground'
        )}>
        {formatMoney(cents, currency)}
      </Text>
      {discounted ? (
        <Text className="text-subtle-foreground text-xs line-through">
          {formatMoney(compareAtCents, currency)}
        </Text>
      ) : null}
    </Row>
  );
}

const CODE_SIZE = {
  subheading: 'text-base',
  heading: 'text-xl',
  title: 'text-2xl',
  display: 'text-3xl',
} as const;

/**
 * The order's short code, set in the monospace stack at a size that can be read
 * across a counter and compared character by character.
 *
 * @param props The code, its size, and whether it is de-emphasised.
 * @returns The code in a muted well.
 */
export function OrderCode({
  code,
  size = 'title',
  muted,
  className,
}: {
  code: string;
  size?: keyof typeof CODE_SIZE;
  muted?: boolean;
  className?: string;
}) {
  return (
    // No `self-start`: the caller's column centres its children, so the well
    // sits under the QR rather than hanging off to one side.
    <View className={cn('bg-muted rounded-md px-3 py-2', className)}>
      <Text
        selectable
        numberOfLines={1}
        className={cn(
          'font-mono font-bold tracking-[3px]',
          CODE_SIZE[size],
          muted ? 'text-muted-foreground' : 'text-foreground'
        )}>
        {code}
      </Text>
    </View>
  );
}

/**
 * A small joined metadata line such as "2 items · 18:45".
 *
 * @param props The items to join; empty entries are dropped.
 * @returns The metadata line.
 */
export function MetaRow({ items, className }: { items: Array<string | null | undefined>; className?: string }) {
  const present = items.filter((item): item is string => Boolean(item));
  return (
    <Text numberOfLines={1} className={cn('text-subtle-foreground text-sm tabular-nums', className)}>
      {present.join(' · ')}
    </Text>
  );
}

/**
 * A quantity pill, for order lines.
 *
 * @param props The quantity.
 * @returns The pill.
 */
export function QuantityPill({ quantity }: { quantity: number }) {
  return (
    <View className="bg-primary-soft min-w-7 items-center rounded-sm px-1.5 py-0.5">
      <Text className="text-primary-strong font-mono text-xs font-bold">{quantity}×</Text>
    </View>
  );
}

const STAT_TEXT = {
  neutral: 'text-muted-foreground',
  brand: 'text-primary-strong',
  success: 'text-success',
  info: 'text-info',
  progress: 'text-progress',
  warning: 'text-warning',
  danger: 'text-destructive',
} as const;

/**
 * A labelled value, for stacking key facts in a summary.
 *
 * @param props Label, value, and optional emphasis or tone.
 * @returns A spread row.
 */
export function StatRow({
  label,
  value,
  emphasis,
  tone,
  numeric,
  size = 'base',
}: {
  label: string;
  value: ReactNode;
  emphasis?: boolean;
  tone?: Tone;
  /**
   * The value's size. `base` is 16 and `lg` is 18, for a total that is the
   * answer on its screen. Separate from `emphasis`, which is the weight,
   * because the merchant's order detail wants the weight and not the size.
   */
  size?: 'base' | 'lg';
  /**
   * Sets the value in tabular figures, so a column of amounts lines up.
   *
   * Opt-in rather than the default: it is right for a stack of numbers and
   * wrong for a single value, and only the caller knows which it has.
   */
  numeric?: boolean;
}) {
  return (
    <Row spread gap="md">
      <Text className={emphasis ? 'text-foreground text-base' : 'text-muted-foreground text-base'}>
        {label}
      </Text>
      {typeof value === 'string' || typeof value === 'number' ? (
        <Text
          className={cn(
            emphasis ? 'font-bold' : 'font-medium',
            size === 'lg' ? 'text-lg' : 'text-base',
            tone ? STAT_TEXT[tone] : emphasis ? 'text-foreground' : 'text-muted-foreground',
            numeric && 'tabular-nums'
          )}>
          {value}
        </Text>
      ) : (
        value
      )}
    </Row>
  );
}

/**
 * A card whose heading sits in a band of its own.
 *
 * Two levels rather than three: the band names the card, and the panel under it
 * holds whatever the card is for. The panel is flush with the card's sides and
 * foot and rounded along its top edge, so the band reads as a heading and the
 * panel as what it holds — the shape a row of figures wanted, and the same shape
 * a list or a set of rows wants, which is why it lives here rather than in the
 * tile.
 *
 * The panel carries no side or bottom border: those are the card's, a pixel
 * underneath, and drawing them twice is how one card starts to look like two.
 *
 * A band may also carry something at its trailing edge, and a line under its
 * title row that spans it. The cook board's tickets use both: the order number is
 * the band's title at the ticket's own scale, the clock sits opposite it, and the
 * booked line — when the kitchen has to start — runs under them both, against the
 * same trailing edge. That is a prop rather than a second component because the
 * shape is the shape — a card that names itself at the top and holds the work
 * under it.
 *
 * The line spans the band rather than sitting in the clock's column on purpose:
 * that column is sized by the clock, so a sentence in it would push the identity
 * column narrower and wrap the name beside it.
 *
 * @param props Title, an optional line of description for the band, an optional
 * trailing node for the band, an optional line under the band's title row, an
 * optional class for the title, content, an optional node under the panel, and
 * optional classes for the card and for the panel — the last for the card whose
 * contents want more or less air around them than the 16 points every other card's
 * do.
 * @returns The card.
 */
export function TitledCard({
  title,
  description,
  action,
  bandFooter,
  bandClassName,
  titleClassName,
  descriptionClassName,
  children,
  className,
  panelClassName,
  panelFooter,
}: {
  title: string;
  description?: string;
  /** Rendered at the band's trailing edge, level with the title. */
  action?: ReactNode;
  /** Rendered under the title row, spanning the band. */
  bandFooter?: ReactNode;
  /**
   * The band's own spacing, and its surface. A *background* here does not reach the
   * wrapper below, which is what paints the band's colour behind the panel's rounded
   * corners — see the comment on it.
   */
  bandClassName?: string;
  /** The title's own type and ink, for a card whose title is not 16/600. */
  titleClassName?: string;
  /** The description's own type and ink, for a band where 12/400 is too quiet. */
  descriptionClassName?: string;
  children: ReactNode;
  className?: string;
  panelClassName?: string;
  /**
   * Rendered under the panel, outside its padding, spanning the card.
   *
   * For the card whose own action belongs at its foot rather than in its band: the
   * cook board's tickets are read across a grid and marking one ready finishes what
   * the panel lists, so the button sits under it. A caller supplies its own padding
   * and top rule, because whether the foot is separated by a rule is the card's
   * business rather than this component's.
   */
  panelFooter?: ReactNode;
}) {
  return (
    <View
      className={cn(
        // `h-full` so a row of cards is one height: the grid cell stretches and
        // the card fills it, rather than each card ending wherever its own
        // content does and the row reading as ragged.
        'bg-card border-border h-full w-full overflow-hidden rounded-xl border',
        className
      )}>
      <View className={cn('bg-muted/50 flex-col gap-1 px-4 py-3', bandClassName)}>
        <View className="flex-row gap-3">
          <View className="min-w-0 flex-1">
            {/* 16/600 and the foreground colour — the size, weight and ink every
                other card heading in the app uses. At 13px and muted the band read as
                a caption above the card rather than as the card's own title, and a
                title is the one line on a card a reader needs whole, so it wraps
                rather than truncating. The band's own background is what keeps it
                quiet now, not the colour of the words. */}
            <Text className={cn('text-foreground text-base font-semibold', titleClassName)}>
              {title}
            </Text>
            {description ? (
              <Text className={cn('text-subtle-foreground mt-0.5 text-xs', descriptionClassName)}>
                {description}
              </Text>
            ) : null}
          </View>
          {action}
        </View>
        {bandFooter}
      </View>
      {/* The band's grey runs on behind the panel's rounded corners. The panel's
          box is the card's colour and its radius cuts the corner squares away, so
          what shows through the arc is whatever is behind it — which was the card's
          own white, leaving a pale wedge between the heading's grey and the sheet
          under it. This wrapper is square, so what shows through the arc is the
          band's colour. */}
      <View className="bg-muted/50 flex-1">
        <View
          className={cn(
            'border-border bg-card flex-1 flex-col gap-1 rounded-t-xl border-t p-4',
            panelClassName
          )}>
          {children}
        </View>
      </View>
      {/* Outside the panel's padding on purpose: a foot is the card's own action
          rather than another row of its contents, so its rule runs the card's whole
          width. */}
      {panelFooter}
    </View>
  );
}

/**
 * A single dashboard metric.
 *
 * The label has a **band of its own**, separated by a rule from the figure below
 * it. Stacked in one column, the label, the number and the hint were three lines
 * of similar weight and the number had to earn its place by size alone; a band
 * makes the label a heading for the figure rather than a line above it.
 *
 * It carried a coloured top rule once, one tone per metric, so a merchant could
 * find "on-time rate" in a row of six without reading the labels. That is the
 * band's job now, and the rule was a second colour on a surface that already has
 * the figure on it — six of them read as a paint chart rather than as figures.
 * What the tones meant is in the hints: "10 of 38 beat the estimate" says what a
 * red border only hinted at.
 *
 * It fills its cell and does not size itself. A tile that carried its own width
 * was as wide as its own hint — Net sales, whose hint is the longest, came out
 * wider than On-time rate, and the row implied a difference in importance that
 * was really a difference in wording; a fixed width instead left dead space
 * beside the last cell whenever the row could not fit another. The grid decides
 * both, from the viewport. Flat too, for the same reason the rule went: the band
 * and the rule already divide the card, and a shadow underneath a divided
 * surface is a third way of saying the same thing.
 *
 * @param props Label, primary value and an optional secondary line.
 * @returns The tile.
 */
export function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <TitledCard title={label}>
      {/* Neither line is clamped: the card is as tall as its own content, and the
          row is levelled by the grid stretching every cell to the tallest one in
          it. Reserving two lines for the hint was the other way round — it made
          every card the height of the longest hint in the whole row, including
          the rows that had none. */}
      {/* `text-3xl` is this screen's figure scale rather than a size chosen here:
          the estimate-learning card's lead number is the same, which is what makes
          the two read as the same kind of thing in different cards. */}
      <Text className="text-3xl font-bold">{value}</Text>
      {hint ? <Text className="text-subtle-foreground text-xs">{hint}</Text> : null}
    </TitledCard>
  );
}
